import React, { useMemo } from 'react';

import { useSortable } from '@dnd-kit/sortable';
import { useLingui } from '@lingui/react/macro';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';

import AnnotatorConstants from '../../annotator.constants';

const useStyles = makeStyles(() => ({
  centerBorderedBox: {
    border: '1px solid gray',
    position: 'relative',
  },
  findingViewerContainer: {
    position: 'relative',
    '&.dragging-image::after': {
      content: '""',
      position: 'absolute',
      backgroundColor: 'transparent',
      zIndex: 20,
      top: '20%',
      left: '9%',
      width: '78%',
      height: '50%',
      border: '5px dashed rgba(155, 155, 155, 0.5)',
      borderRadius: '16px',
    },
    '&.dragging-image::before': {
      content: '""',
      position: 'absolute',
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 20,
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
    },
    '&.is-over-image::after': {
      zIndex: 30,
      borderColor: 'white',
    },
    '&.findingViewerContainerDisabled': {
      pointerEvents: 'none',
    },
  },
  hoveredItem: {
    cursor: 'pointer',
    borderColor: 'white',
  },
  imageOverlay: {
    width: '100%',
    height: '96%',
    position: 'absolute',
    top: '0',
    zIndex: 10,
    left: '0',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  noImagesMessage: {
    color: 'white',
    fontWeight: 100,
    textAlign: 'center',
  },
}));

interface Props {
  children: React.ReactNode;
  currentImage: any;
  imagesPerRow: number;
  numberOfRows: number;
  imageCount?: number;
  onSetSelectedImage: React.MouseEventHandler<HTMLElement>;
  currentImageFilter: string;
  currentlyHoveredImageId: string;
  onSetCurrentlyHoveredImageId: React.Dispatch<React.SetStateAction<string>>;
  currentIndex: number;
  noImagesOnCurrentView: boolean;
  disableDrag: boolean;
}

export const ImageLayoutContainer = ({
  currentImage,
  currentImageFilter,
  imagesPerRow,
  numberOfRows,
  imageCount,
  onSetSelectedImage,
  currentlyHoveredImageId,
  onSetCurrentlyHoveredImageId,
  currentIndex,
  noImagesOnCurrentView,
  children,
  disableDrag,
}: Props) => {
  const classes = useStyles();
  const {
    attributes,
    listeners,
    setNodeRef,
    isOver: isDraggingOver,
    over: isDragging,
  } = useSortable({
    id: (currentImage.fmxIndex ?? 999).toString(),
    disabled: disableDrag,
  });
  const { t } = useLingui();

  const getClassNames = () => {
    let className = classes.findingViewerContainer;
    if (currentImage?.objectId === currentlyHoveredImageId) {
      className += ` ${classes.hoveredItem}`;
    }
    if (isDragging) {
      className += ' dragging-image';
    }
    if (isDraggingOver) {
      className += ' is-over-image';
    }
    // Only disable pointer events for confidence score filter, not for Series view
    // Series view should allow clicking but disable dragging
    if (disableDrag && currentImageFilter !== AnnotatorConstants.BITEWING) {
      className += ` findingViewerContainerDisabled`;
    }

    return className;
  };

  const noImagesMessageToDisplay = (imageFilter: string) => {
    if (!noImagesOnCurrentView) {
      return '';
    }
    if (imageFilter === AnnotatorConstants.BITEWING) {
      return t`No bitewing images found for this patient`;
    }
    if (imageFilter === AnnotatorConstants.FMX) {
      return t`No bitewings or periapical images found for this patient`;
    }
  };

  const { maxHeight, layoutHeight } = useMemo(() => {
    let _maxHeight = 0.33;
    let _layoutHeight = 0.75;

    if (currentImageFilter === AnnotatorConstants.FMX) {
      _layoutHeight = 1 / numberOfRows;
    } else {
      _layoutHeight = 0.75 / numberOfRows;
    }

    if (imageCount) {
      switch (imageCount) {
        case 1:
          _maxHeight = 0.9;
          _layoutHeight = 0.9;
          break;
        case 2:
          _maxHeight = 0.75;
          break;
        case 3:
          _maxHeight = 0.5;
          break;
        case 4:
          _maxHeight = 0.33;
          break;
      }
    }

    return {
      maxHeight: _maxHeight,
      layoutHeight: _layoutHeight,
    };
  }, [currentImageFilter, numberOfRows, imageCount]);

  return (
    <React.Fragment key={currentImage.objectId}>
      <Box
        data-testid="image-container"
        width={1 / imagesPerRow}
        height={layoutHeight}
        maxHeight={maxHeight}
        margin="0"
        onClick={onSetSelectedImage}
        className={getClassNames()}
        onMouseEnter={() => onSetCurrentlyHoveredImageId(currentImage.objectId)}
        onMouseLeave={() => onSetCurrentlyHoveredImageId('')}
        ref={setNodeRef}
        {...attributes}
        {...(disableDrag ? {} : listeners)}
      >
        {currentImage.objectId && children}
        {currentImage.objectId && (
          // Don't show the finding viewer if this is just an empty box (i.e. if we filled this index with a {})
          <div className={classes.imageOverlay} />
        )}
      </Box>

      {currentImageFilter === AnnotatorConstants.BITEWING &&
        noImagesOnCurrentView &&
        currentIndex == 0 && (
          <Box
            position="absolute"
            left="0"
            right="0"
            marginLeft="auto"
            marginRight="auto"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <h3 className={classes.noImagesMessage}>
              {noImagesMessageToDisplay(currentImageFilter)}
            </h3>
          </Box>
        )}

      {currentImageFilter === AnnotatorConstants.FMX && currentIndex === 8 && (
        // If we're looking at the 10th (index 9) image, we need to show a gap of 3/7 as spacing (for bitewing row)
        <Box
          width={3 / imagesPerRow}
          height={1 / numberOfRows}
          display="flex"
          justifyContent="center"
        >
          <Box
            width={3 / 4}
            height={0.8}
            className={classes.centerBorderedBox}
            display="flex"
            justifyContent="center"
            alignItems="center"
            padding={1}
          >
            <h3 className={classes.noImagesMessage}>
              {noImagesMessageToDisplay(currentImageFilter)}
            </h3>
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};
